import React, { useId, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormHelperText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const focusStyle = {
  "&:focus-visible": {
    outline: "3px solid #111",
    outlineOffset: "3px",
  },
};

export default function ShippingMethodSelector({
  value,
  onChange,
  error = false,
  errorId,
  labelledBy,
  describedBy,
}) {
  const [openDialog, setOpenDialog] = useState(false);

  const selectRef = useRef(null);
  const navigate = useNavigate();

  const generatedId = useId();

  const labelId = `shipping-method-label-${generatedId}`;
  const selectId = `shipping-method-select-${generatedId}`;
  const helperTextId = `shipping-method-help-${generatedId}`;
  const dialogTitleId = `shipping-dialog-title-${generatedId}`;
  const dialogDescriptionId =
    `shipping-dialog-description-${generatedId}`;

  const handleSelect = (event) => {
    const selected = event.target.value;

    if (selected === "משלוח") {
      setOpenDialog(true);

      // עדיין לא מאשרים משלוח עד לחיצה על "אישור"
      onChange("");
      return;
    }

    onChange("איסוף עצמי");
  };

  const handleApproveDelivery = () => {
    onChange("משלוח");
    setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);

    // מחזיר את הפוקוס לבחירת שיטת המשלוח
    window.setTimeout(() => {
      selectRef.current?.focus();
    }, 0);
  };

  const handleOpenShippingPolicy = () => {
    setOpenDialog(false);
    navigate("/shipping-policy");
  };

  const ariaDescribedBy = [
    helperTextId,
    describedBy,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      sx={{
        mt: 3,
        width: "100%",
        direction: "rtl",
      }}
    >
      <FormControl
        fullWidth
        error={error}
        sx={{
          minWidth: {
            xs: "100%",
            sm: 260,
          },
        }}
      >
        <Select
          inputRef={selectRef}
          id={selectId}
          value={value || ""}
          onChange={handleSelect}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Box
                  component="span"
                  sx={{ color: "text.secondary" }}
                >
                  בחר שיטת משלוח
                </Box>
              );
            }

            return selected === "איסוף עצמי"
              ? "איסוף עצמי מהחנות"
              : "משלוח";
          }}
          inputProps={{
            "aria-label": "בחירת שיטת משלוח",
            "aria-required": "true",
            "aria-invalid": error ? "true" : "false",
            "aria-describedby":
              error && errorId
                ? errorId
                : undefined,
          }}
          MenuProps={{
            PaperProps: {
              dir: "rtl",
            },
            MenuListProps: {
              "aria-label": "אפשרויות שיטת משלוח",
            },
          }}
          sx={{
            direction: "rtl",
            minHeight: 48,

            "& .MuiSelect-select": {
              textAlign: "right",
              display: "flex",
              alignItems: "center",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#111",
              borderWidth: "2px",
            },

            ...focusStyle,
          }}
        >
          <MenuItem value="איסוף עצמי">
            איסוף עצמי מהחנות
          </MenuItem>

          <MenuItem value="משלוח">
            משלוח
          </MenuItem>
        </Select>
      </FormControl>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        dir="rtl"
        fullWidth
        maxWidth="sm"
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          id={dialogTitleId}
          component="h2"
          sx={{
            fontWeight: 700,
            textAlign: "right",
          }}
        >
          אישור שיטת משלוח
        </DialogTitle>

        <DialogContent>
          <Box
            id={dialogDescriptionId}
            sx={{
              textAlign: "right",
            }}
          >
            <Typography
              component="p"
              sx={{
                fontSize: "1.125rem",
                mt: 1,
              }}
            >
              כרגע המשלוחים הם לירושלים והסביבה בלבד.
            </Typography>

            <Typography
              component="p"
              sx={{
                fontSize: "1.125rem",
                mt: 1,
              }}
            >
              התשלום עבור המשלוח ייגבה בנפרד.
            </Typography>

            <Typography
              component="p"
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 2,
              }}
            >
              לאישור בחירת המשלוח לחצו על כפתור "אישור".
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            gap: 1,
            px: 3,
            pb: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={handleOpenShippingPolicy}
            sx={{
              minHeight: 44,
              color: "#AD1457",
              borderColor: "#AD1457",

              "&:hover": {
                borderColor: "#880e4f",
                backgroundColor: "#fff5f8",
              },

              ...focusStyle,
            }}
          >
            למחירון משלוחים
          </Button>

          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            <Button
              type="button"
              variant="text"
              onClick={handleCloseDialog}
              sx={{
                minHeight: 44,
                color: "#37474f",

                ...focusStyle,
              }}
            >
              ביטול
            </Button>

            <Button
              type="button"
              variant="contained"
              onClick={handleApproveDelivery}
              autoFocus
              sx={{
                minHeight: 44,
                backgroundColor: "#D81B60",

                "&:hover": {
                  backgroundColor: "#AD1457",
                },

                ...focusStyle,
              }}
            >
              אישור משלוח
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}